/**
 * Numeric hierarchy levels for each role.
 * Higher numbers indicate greater privilege.
 */
/**
 * Mirrors the backend ROLE_HIERARCHY (api/src/auth/guards/roles.guard.ts).
 * One coherent scale spanning the legacy KDU roles and the spinoff six-role
 * model. Keep these two tables in sync.
 */
const HIERARCHY: Record<string, number> = {
  // Platform band (cross-tenant)
  PlatformAdmin: 100,
  Admin: 100, // legacy platform admin
  PlatformUser: 90,

  // Customer band (one tenant)
  CustomerSuperUser: 60,
  CustomerEditor: 50,
  Organizer: 50, // legacy operational role

  // Read-only / guest-ish band
  Delegate: 20,
  CustomerUser: 10,
  Attendee: 10,
};

/**
 * Roles permitted to manage a tenant's users (invite/list/remove) — the sole
 * capability separating Customer Super User from Customer Editor. Mirrors the
 * backend TENANT_USER_MANAGER_ROLES.
 */
const TENANT_USER_MANAGER_ROLES = new Set<string>([
  'PlatformAdmin',
  'Admin',
  'PlatformUser',
  'CustomerSuperUser',
]);

/** True if the given role may manage a tenant's users. */
export function canManageTenantUsers(role: string | undefined | null): boolean {
  return !!role && TENANT_USER_MANAGER_ROLES.has(role);
}

/**
 * Returns true when the user's role is at or above the required role
 * in the hierarchy. Unknown roles are treated as level 0 (no access).
 *
 * @param userRole - The role of the current user
 * @param requiredRole - The minimum role required for access
 */
export function isAtLeast(userRole: string, requiredRole: string): boolean {
  return (HIERARCHY[userRole] ?? 0) >= (HIERARCHY[requiredRole] ?? 0);
}

/**
 * Whether a first-login forced password change should be enforced for this
 * role. Staff/operator/customer accounts are created with a temp password and
 * MUST change it on first login. Legacy KDU guest roles (Attendee/Delegate)
 * are intentionally exempt — they log in with a ticket number and the forced
 * flow caused repeated lockouts, so for them /change-password is voluntary.
 */
export function enforcesPasswordChange(role: string | undefined | null): boolean {
  if (!role) return false;
  return role !== 'Attendee' && role !== 'Delegate';
}
